import range from 'lodash/range';
import { jsx } from 'panel';
import { SvgIconEl } from 'common/components/svg-icon';
import { TooltipEl } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { handleKeyPress } from 'common/util/focusable';
import { SidenavItemEl } from './sidenav-item';
import { SIDENAV_DEFAULT_WIDTH } from './constants';

function Loader() {
  return jsx("div", {
    sel: '.loader'
  }, jsx("div", {
    sel: '.mp-skeleton-header'
  }), range(9).map(_ => jsx("div", {
    sel: '.mp-skeleton-large'
  })));
}

function SectionContent(_ref) {
  let {
    section,
    sectionIdx,
    isExpanded,
    selectedItem,
    dragDropEnabled,
    sectionDragDisabled,
    itemHandlers,
    confirmNavigateFunc
  } = _ref;

  if (isExpanded) {
    if (section.items.length) {
      return jsx(null, null, section.items.map((item, idx) => jsx(SidenavItemEl, {
        attrs: {
          item: Object.assign(Object.assign({}, item), {
            itemIdx: idx,
            sectionIdx
          }),
          'is-selected': selectedItem && item.value === selectedItem.value,
          'is-draggable': dragDropEnabled,
          'is-disabled': sectionDragDisabled
        },
        on: itemHandlers,
        props: {
          confirmNavigateFunc
        }
      })));
    } else {
      return jsx("div", {
        sel: '.empty-notice'
      }, section.emptyNotice || "This section is empty");
    }
  } else if (selectedItem) {
    return jsx(SidenavItemEl, {
      attrs: {
        item: selectedItem,
        'is-selected': true,
        'is-draggable': false
      }
    });
  }

  return null;
}

function SidenavSection(_ref2) {
  let {
    section,
    sectionIdx,
    selectedItem,
    isExpanded,
    dragDropEnabled,
    sectionDragDisabled,
    itemHandlers,
    onToggleSection,
    confirmNavigateFunc
  } = _ref2;
  return jsx("div", {
    sel: '.section',
    class: {
      'active-dragging-disabled': sectionDragDisabled
    }
  }, jsx("div", {
    sel: '.section-header',
    attrs: {
      tabindex: 0
    },
    on: {
      click: () => onToggleSection(section.label),
      keypress: handleKeyPress
    }
  }, jsx(SvgIconEl, {
    sel: '.caret',
    attrs: {
      icon: isExpanded ? "filled-caret-down" : "filled-caret-right"
    }
  }), jsx("div", {
    sel: '.section-header-label'
  }, section.label)), jsx("div", {
    sel: '.section-items-list'
  }, jsx(SectionContent, {
    section: section,
    sectionIdx: sectionIdx,
    isExpanded: isExpanded,
    selectedItem: selectedItem,
    dragDropEnabled: dragDropEnabled,
    sectionDragDisabled: sectionDragDisabled,
    confirmNavigateFunc: confirmNavigateFunc,
    itemHandlers: itemHandlers
  })));
}

export function template() {
  const {
    sectionIndex: selectedSectionIndex,
    itemIndex: selectedSidenavIndex
  } = this.helpers.getSelectedIndices();
  const sections = this.attr("sections");
  const itemHandlers = {
    itemDrop: this.helpers.onItemDrop,
    itemDragStart: this.helpers.onItemDragStart,
    itemDragEnd: this.helpers.onItemDragEnd,
    itemClick: this.helpers.onSelectItem,
    overflowMenuChange: this.helpers.onItemOverflowMenuChange,
    overflowMenuSelect: this.helpers.onItemOverflowMenuSelect
  };
  const sidenavContent = this.attr("loading") ? jsx(Loader, null) : sections.map((section, index) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== index;
    const isExpanded = !this.state.collapsedSectionSet.has(section.label);
    const selectedItemInSection = selectedSectionIndex === index ? sections[index].items[selectedSidenavIndex] : null;
    return jsx(SidenavSection, {
      section: section,
      sectionIdx: index,
      selectedItem: selectedItemInSection,
      index: index,
      isExpanded: isExpanded,
      dragDropEnabled: this.attr("dragdrop-enabled"),
      sectionDragDisabled: sectionDragDisabled,
      onToggleSection: this.helpers.toggleSection,
      confirmNavigateFunc: this.confirmNavigateFunc,
      itemHandlers: itemHandlers
    });
  });
  const header = this.attr("header");
  const footerActions = this.helpers.getFooterActions();
  return jsx("div", {
    sel: '.wrapper',
    class: {
      collapsed: this.state.isCollapsed,
      'is-hovering': this.state.isHovering,
      'is-resizing': this.state.isResizing
    },
    style: {
      '--sidenav-width': "".concat(this.state.width, "px")
    },
    on: {
      mouseleave: this.helpers.onSectionsMouseleave
    },
    attrs: {
      elref: "wrapper"
    }
  }, header ? jsx("div", {
    sel: '.header',
    class: {
      'is-scrolling': this.state.isScrolling && this.helpers.isExpandedStyle()
    }
  }, jsx("div", {
    sel: '.title'
  }, header.title), jsx("div", {
    sel: '.header-cta'
  }, jsx(ButtonEl, {
    attrs: {
      theme: ButtonTheme.Tertiary,
      icon: header.cta.icon,
      elref: "headerCta"
    },
    on: {
      click: () => this.helpers.onHeaderActionClick(header.cta.value)
    }
  }), jsx(TooltipEl, {
    attrs: {
      placement: Placement.Right
    }
  }, header.cta.label))) : null, jsx("div", {
    sel: '.sections-container',
    on: {
      mouseenter: this.helpers.onSectionsMouseenter
    },
    hook: {
      insert: this.helpers.onSectionsInserted
    },
    attrs: {
      elref: "sectionsContainer"
    },
    class: {
      'is-overflow-menu-open': this.state.isOverflowMenuOpen
    }
  }, jsx("div", {
    sel: '.sections'
  }, sidenavContent)), (footerActions === null || footerActions === void 0 ? void 0 : footerActions.length) ? jsx("div", {
    sel: '.footer',
    class: {
      'active-dragging-disabled': Number.isInteger(this.state.activeDragSectionIdx)
    }
  }, footerActions.map(cta => {
    return jsx("div", {
      sel: '.footer-cta',
      on: {
        click: () => this.helpers.onFooterActionClick(cta.value),
        keypress: handleKeyPress
      },
      attrs: {
        value: cta.value,
        tabindex: 0
      }
    }, jsx(SvgIconEl, {
      attrs: {
        icon: cta.icon
      }
    }), jsx("div", {
      sel: '.footer-cta-label'
    }, cta.label), !this.helpers.isExpandedStyle() ? jsx(TooltipEl, {
      attrs: {
        placement: Placement.Right
      }
    }, cta.label) : null);
  })) : null, this.helpers.isExpandedStyle() ? jsx("div", {
    sel: '.resize-handle',
    on: {
      mousedown: this.helpers.startResizing
    },
    class: {
      'default-width': this.state.width === SIDENAV_DEFAULT_WIDTH
    },
    attrs: {
      elref: "resizeHandle"
    }
  }) : null);
}